import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useBoundStore} from "../../../Store/store";
import {useEffect} from "react";
import {BaseCard} from "../../../Components/Cards/BaseCard";
import * as React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import {ShiftInfoCard} from "../../../Components/Cards/ShiftInfoCard";
import {SelectInput} from "../../../Components/Form/SelectInput";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {PrimaryButton} from "../../../Components/Buttons/PrimaryButton";
import {ControlledSwitch} from "../../../Components/Form/ControlledSwitch";
import toast from "react-hot-toast";
import ApplicationDetailImage from "../../../Assets/images/illustrations/application_details.svg";

export const AdminApplicationDetail = () => {
    const {
        applicationId
    } = useParams();

    const navigate = useNavigate();
    const {t} = useTranslation();

    const getApplicationDetail = useBoundStore(state => state.getApplicationDetail);
    const applicationDetail = useBoundStore(state => state.applicationDetail);
    const applicationDetailNotFound = useBoundStore(state => state.applicationDetailNotFound);
    const updateApplication = useBoundStore(state => state.updateApplication);

    useEffect(() => {
        getApplicationDetail(applicationId);
    }, [getApplicationDetail, applicationId]);

    useEffect(() => {
        if(applicationDetail?.id){
            reset({
                status: applicationDetail?.status,
                in_progress: applicationDetail?.in_progress,
            })
        }
    }, [applicationDetail]);

    const schema = yup.object({
        status: yup.mixed().required(),
        in_progress: yup.boolean().nullable(),
    }).required()

    const {
        reset,
        handleSubmit,
        formState: {errors},
        getValues,
        setValue,
        control,
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            status: applicationDetail?.status,
            in_progress: applicationDetail?.in_progress,
        }
    });

    const onSubmit = (data) => {
        console.log(data);

        const transformedData = {
            status: data.status,
            in_progress: data.in_progress === "" ? false : data.in_progress,
        }

        updateApplication(applicationId, transformedData).then((result) => {
            if (!result) return;
            toast.success(t('applications_module.toast_updated'));
            navigate('/admin/applications');
        });
    }

    if (applicationDetailNotFound) {
        return (
            <div>
                <h1 className={'text-2xl font-semibold mb-4'}>{t('applications_module.detail_title')}</h1>
                <BaseCard border={false}>
                    <div className={'flex flex-col items-center text-center py-12'}>
                        <img src={ApplicationDetailImage} alt="" className={'w-40 mb-6 opacity-60'}/>
                        <h2 className={'text-xl font-semibold text-gray-900'}>{t('applications_module.not_found_title')}</h2>
                        <p className={'text-sm text-gray-500 mt-2 max-w-md'}>{t('applications_module.not_found_description')}</p>
                        <div className={'mt-6'}>
                            <PrimaryButton onClick={() => navigate('/admin/applications')}>
                                {t('applications_module.back_to_overview')}
                            </PrimaryButton>
                        </div>
                    </div>
                </BaseCard>
            </div>
        );
    }

    return (
        <div>
            <div>
                <div className={'flex flex-row justify-between mb-4 items-center'}>
                    <div>
                        <h1 className={'text-2xl font-semibold'}>{t('applications_module.detail_title')}</h1>
                        <div className={"flex flex-row group items-center"} onClick={() => {
                            navigate('/admin/applications');
                        }}>
                            <FontAwesomeIcon icon={faArrowLeft}
                                             className={'text-sm transition-all group-hover:mr-2 duration-500 cursor-pointer'}/>

                            <div
                                className={'text-sm text-gray-500 my-auto cursor-pointer ml-2'}>{t('general.actions.back_to_overview')}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                <div className={'md:col-span-2'}>
                    <BaseCard border={false}>
                        <div className={'flex flex-col md:flex-row justify-between'}>
                            <div>
                                <h2 className={'text-xl font-semibold'}>{t('applications_module.info_title')}</h2>
                            </div>
                            <div className={'w-fit flex flex-row gap-2'}>
                                {
                                    applicationDetail?.status === 'applied' && (
                                        <div
                                            className={'px-2 py-1 bg-gray-300 text-gray-900 rounded-full text-xs opacity-70'}>{t('general.status.applied')}
                                        </div>
                                    )
                                }

                                {
                                    applicationDetail?.status === 'rejected' && (
                                        <div
                                            className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70'}>{t('general.status.rejected')}
                                        </div>
                                    )
                                }

                                {
                                    applicationDetail?.status === 'accepted' && (
                                        <div
                                            className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('general.status.accepted')}
                                        </div>
                                    )
                                }

                                {
                                    !! applicationDetail?.in_progress && (
                                        <div
                                            className={'px-2 py-1 bg-blue-300 text-blue-900 rounded-full text-xs opacity-70'}>{t('general.status.in_progress')}
                                        </div>
                                    )
                                }

                            </div>
                        </div>

                        <div className={'mt-4'}>
                            <p className={'text-gray-900 flex flex-row gap-2'}>
                                <span className="font-semibold">{t('applications_module.applied_on')}: </span>
                                {applicationDetail?.applied_at?.formatted}
                            </p>
                            <p className={'text-gray-900 flex flex-row gap-2'}>
                                <span className="font-semibold">{t('applications_module.accepted_on')}: </span>
                                {applicationDetail?.accepted_at?.formatted ?? '-'}
                            </p>
                            {applicationDetail?.applied_as_statuut && applicationDetail.applied_as_statuut !== 'freelance' && (
                                <p className="mt-2">
                                    <span className="text-xs text-purple-800 bg-purple-50 inline-block px-2 py-0.5 rounded">
                                        {t('applications_module.pratoflex_note', {statuut: t(`statuten.${applicationDetail.applied_as_statuut}`, applicationDetail.applied_as_statuut)})}
                                    </span>
                                </p>
                            )}
                        </div>

                        {applicationDetail?.status === 'rejected' ? (
                            <p className={'text-sm text-gray-500 mt-4'}>
                                {t('applications_module.status_locked')}
                            </p>
                        ) : (
                            <form className="w-full mt-4" onSubmit={handleSubmit(onSubmit)}>
                                <SelectInput
                                    name={'status'}
                                    options={applicationDetail?.status === 'accepted'
                                        ? [
                                            {id: 1, value: 'accepted', label: t('registrations.status_accepted')},
                                            {id: 2, value: 'rejected', label: t('general.status.rejected')},
                                        ]
                                        : [
                                            {id: 1, value: 'applied', 'label': t('general.status.applied')},
                                            {id: 2, value: 'rejected', label: t('general.status.rejected')},
                                            {id: 3, value: 'accepted', label: t('registrations.status_accepted')},
                                        ]
                                    }
                                    setValue={setValue}
                                    control={control}
                                    value={getValues('status') ?? null}
                                    placeholder={t('general.fields.status')}
                                    translationIndex={t('tables.status')}
                                />

                                <div className="mt-4">
                                    <ControlledSwitch
                                        control={control}
                                        name={'in_progress'}
                                        getValues={getValues}
                                        errors={errors}
                                        setValue={setValue}
                                        translationIndex={t('registrations.is_pending')}
                                    />
                                </div>

                                <div className={'mt-4 flex flex-row justify-end'}>
                                    <div className={'w-fit'}>
                                        <PrimaryButton onClick={handleSubmit(onSubmit)}>
                                            {t('general.actions.save')}
                                        </PrimaryButton>
                                    </div>
                                </div>
                            </form>
                        )}
                    </BaseCard>
                </div>

                <BaseCard border={false}>
                    <ShiftInfoCard shift={applicationDetail?.shift} overrideImage={ApplicationDetailImage}/>
                </BaseCard>
            </div>
        </div>
    )
}